/**
 * Collapses progress rows that read the same into one row per label
 * (SE-8646, SE-8783).
 * @remarks
 * v2 reports each tool call under the tool's own camelCase name, and the tool
 * set is open-ended, so every tool without an entry of its own resolves to the
 * same generic label. Three tool calls in a row then showed as three identical
 * "Checking our records" rows — to the user, one step listed three times. The
 * rows are merged on the label the user reads rather than on the agent, so a
 * newly added tool cannot bring the duplicate back.
 *
 * SE-8783: the agent loop alternates reasoning with tool calls, so the same
 * label also returns after a different step — one prompt showed "Working out
 * the answer" four times. Every label now shows once, whatever the prompt: a
 * repeat reuses the row in the position the label first appeared, and that
 * row shows running again while the repeat runs.
 */
import type { IAgentProgress } from "@/stores/knowledgeGraph";

/** Which status a merged step shows, most significant first. */
const STATUS_PRECEDENCE: readonly IAgentProgress["status"][] = [
  "running",
  "error",
  "pending",
  "complete",
];

/**
 * The status a merged step shows.
 * @param rows - Every row that reads as this step
 * @returns Running if any row is running, else error if any errored, else
 * pending if any is pending, else complete
 * @remarks
 * Taken across every occurrence, not just the latest: a later repeat that
 * completes must not hide an earlier occurrence that failed, and two tool
 * calls under one label can run at once.
 */
const mergedStatus = (
  rows: readonly IAgentProgress[]
): IAgentProgress["status"] =>
  STATUS_PRECEDENCE.find(status => rows.some(r => r.status === status)) ??
  "complete";

/**
 * Merges rows whose display label is identical into one row per label.
 * @param rows - Progress rows in arrival order
 * @param labelOf - Resolves the label the user sees for an agent
 * @returns One row per label, in the order each label first appeared: the
 * label's most recent row, carrying the label's merged status
 */
export const collapseProgressSteps = (
  rows: readonly IAgentProgress[],
  labelOf: (agent: string) => string
): IAgentProgress[] =>
  [
    ...rows
      .reduce<Map<string, IAgentProgress[]>>((byLabel, row) => {
        const label = labelOf(row.agent);
        return new Map(byLabel).set(label, [
          ...(byLabel.get(label) ?? []),
          row,
        ]);
      }, new Map())
      .values(),
  ].map(group =>
    group.length === 1
      ? group[0]
      : { ...(group.at(-1) as IAgentProgress), status: mergedStatus(group) }
  );
